
import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import {
  cleanupCommissionsPayable,
  loadCommissions,
} from "../../../redux/features/commissions/actions";
import { useAppSelector } from "../../../redux/hooks";
import { CommissionsTable } from "./CommissionsTable";

/**
 * Per-sales-rep default start invoice dates used to pre-populate the cleanup modal.
 * Keyed by sales rep ID.
 */
const cleanupStartInvoiceDateDefaults: Record<number, string> = {
  8: '2024-06-01', // Josh Felice
  28: '2025-07-01', // Jonathan Cauchon 
};

/**
 * Returns the default cleanup start invoice date for a given sales rep.
 * Falls back to `'2026-01-01'` if no per-rep default is configured.
 *
 * @param salesRepId - The ID of the sales rep.
 * @returns An ISO date string to pre-fill as the start invoice date in the cleanup modal.
 */
const getDefaultCleanupStartInvoiceDate = (salesRepId: number) => (
  cleanupStartInvoiceDateDefaults[salesRepId] ?? '2026-01-01'
);

/**
 * Displays unpaid commissions for the given sales rep(s) in a tabular view.
 *
 * On mount (and whenever `salesRepIds` or `commissionSource` changes) it fetches
 * the payable commission records from the API via {@link loadCommissions} and
 * renders them inside {@link CommissionsTable}.
 *
 * Both legacy (`'Paid'`) and Hydra (`'paid'`) invoice states are included in the
 * query so that the table receives a unified, source-agnostic list.
 *
 * @param salesRepIds - Array of sales rep IDs whose unpaid commissions should be displayed.
 */
export const CommissionsPayable = ({ salesRepIds }: { salesRepIds: number[] }) => {
  const salesReps = useAppSelector(state => state.commissions.salesRepList);
  const commissionsList = useAppSelector(state => state.commissions.commissionsList);
  const commissionSource = useAppSelector(state => state.commissions.commissionSource);
  const [ cleanupModalOpen, setCleanupModalOpen ] = useState(false);
  const [ cleanupStartInvoiceDate, setCleanupStartInvoiceDate ] = useState('');
  const [ isCleanupLoading, setIsCleanupLoading ] = useState(false);

  // The cleanup action is single-rep; use the first selected rep when applicable.
  const primaryRepId = salesRepIds[0] ?? 0;

  const legacyInvoiceStates = ['Paid'];
  const hydraInvoiceStates = ['paid'];
  const invoiceStates = JSON.stringify({
    legacyInvoiceStates,
    hydraInvoiceStates,
  });
  const salesRepName = useMemo(
    () => salesReps.find((salesRep) => salesRep.id === primaryRepId),
    [ salesReps, primaryRepId ],
  );

  useEffect(() => {
    loadCommissions(
      salesRepIds,
      `/payable?invoiceStates=${invoiceStates}&paid=false&unpaid=true&source=${commissionSource}`,
    );
  }, [salesRepIds, commissionSource]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Triggers the cleanup action with the primary `salesRepId` and selected `cleanupStartInvoiceDate`.
   * Sets `isCleanupLoading` to `true` for the duration of the request, disabling the confirm button
   * and showing a "Cleaning up…" label. Closes the modal when the request settles (success or error).
   */
  const handleCleanup = async () => {
    setIsCleanupLoading(true);
    try {
      await cleanupCommissionsPayable(primaryRepId, cleanupStartInvoiceDate);
    } finally {
      setIsCleanupLoading(false);
      setCleanupModalOpen(false);
    }
  };

  return (
    <>
      <Row className='pt-2'>
        <Col className='text-end'>
          <Button
            variant='warning'
            onClick={() => {
              setCleanupStartInvoiceDate(getDefaultCleanupStartInvoiceDate(primaryRepId));
              setCleanupModalOpen(true);
            }}
            disabled={salesRepIds.length !== 1}
            title={salesRepIds.length !== 1 ? 'Select exactly one sales rep to clean up' : undefined}
          >
            Clean up
          </Button>
        </Col>
      </Row>
      <CommissionsTable
        commissionsList={commissionsList}
        tab='payable'
        commissionSource={commissionSource}
        showCommissionRates={true}
        salesRepIds={salesRepIds}
        invoiceStates={invoiceStates}
      />

      <Modal show={cleanupModalOpen} onHide={() => setCleanupModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Clean up payable commissions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Sales rep:</strong>{' '}
            {salesRepName
              ? `${salesRepName.user.firstName} ${salesRepName.user.lastName}`
              : 'Select a sales rep'}
          </p>
          <Form.Group className='mb-3' controlId='cleanupStartInvoiceDate'>
            <Form.Label>Start invoice date for cleanup</Form.Label>
            <Form.Control
              type='date'
              value={cleanupStartInvoiceDate}
              onChange={(event) => setCleanupStartInvoiceDate(event.target.value)}
            />
          </Form.Group>
          <p className='mb-0'>
            This will clean up all non project work (non daily or one time billing) for the above sales rep.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setCleanupModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant='warning'
            onClick={handleCleanup}
            disabled={isCleanupLoading || !primaryRepId || !cleanupStartInvoiceDate}
          >
            {isCleanupLoading ? 'Cleaning up…' : 'Clean up'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
